/**
 * Pump Command Center — flat module navigation (logical order).
 */

export const INTELLIGENCE_MODULE_IDS = [
  'owner-dashboard',
  'business-insights',
  'data-analytics',
  'operations-intelligence',
  'accounting',
];

export const PUMP_HUB_NAV = [
  {
    group: 'Overview',
    items: [
      { id: 'dashboard', label: 'Summary', description: 'Health score and quick navigation', step: null },
    ],
  },
  {
    group: 'Intelligence & Performance',
    items: [
      { id: 'owner-dashboard', label: 'Owner Dashboard', description: 'Sales, profit, fuel mix, and KPI charts', step: 1 },
      { id: 'business-insights', label: 'Business Intelligence', description: 'Staffing, cash, and profitability checks', step: 2 },
      { id: 'data-analytics', label: 'Data Analytics', description: 'Executive health and deep analysis', step: 3 },
      { id: 'operations-intelligence', label: 'Operations Intelligence', description: 'Nozzle queue and employee pairing', step: 4 },
      { id: 'accounting', label: 'Accounting Integrity', description: 'Ledger scan, fixes, and audit trail', step: 5 },
      { id: 'owner-fund-analytics', label: "Owner's Fund Analytics", description: 'Fund reconciliation, P&L and balance sheet insights', step: 6 },
    ],
  },
  {
    group: 'Pump Administration',
    items: [
      { id: 'users', label: 'Users & Access', description: 'Staff accounts and open pump as user', step: null },
      { id: 'subscription', label: 'Subscription', description: 'Plans, renewals, and billing', step: null },
      { id: 'invoice', label: 'Invoice', description: 'GST settings and draft invoices', step: null },
      { id: 'alerts', label: 'Alerts', description: 'Entry issues and warnings', step: null },
    ],
  },
];

export const MODULE_META = {
  dashboard: {
    title: 'Pump summary',
    description: 'Overview of business health and quick links to each intelligence module.',
  },
  'owner-dashboard': {
    title: 'Owner Dashboard',
    description: 'Sales, profit, fuel mix, stock, expenses, and employee performance — the same view pump owners see.',
    icon: '📊',
    tone: 'blue',
  },
  'business-insights': {
    title: 'Business Intelligence',
    description: 'Run focused diagnostic checks — staffing, nozzle share, cash irregularities, and profitability.',
    icon: '💡',
    tone: 'violet',
  },
  'data-analytics': {
    title: 'Data Analytics',
    description: 'Executive health score, deep analysis, cash and stock intelligence, and profit playbook.',
    icon: '📈',
    tone: 'teal',
  },
  'operations-intelligence': {
    title: 'Operations Intelligence',
    description: 'Nozzle queue proxy, employee tiers, and best shift pairings.',
    icon: '⛽',
    tone: 'indigo',
  },
  accounting: {
    title: 'Accounting Integrity Check',
    description: 'Scan ledger mismatches section by section, apply guided corrections, and review the audit trail.',
    icon: '🧾',
    tone: 'amber',
  },
  'owner-fund-analytics': {
    title: "Owner's Fund Analytics",
    description: 'Spot irregularities where daily P&L does not match balance-sheet owner fund — with P&L and balance sheet insights to improve profit.',
    icon: '💰',
    tone: 'emerald',
  },
  users: {
    title: 'Users & Access',
    description: 'Manage pump staff, reset passwords, lock accounts, or open the pump as a specific user.',
  },
  subscription: {
    title: 'Subscription & Billing',
    description: 'Add plans, renew subscriptions, mark payments, or activate a newly registered pump.',
  },
  invoice: {
    title: 'Invoice Settings',
    description: 'Configure GST, place of supply, and draft client invoices.',
  },
  alerts: {
    title: 'Pump Alerts',
    description: 'Data entry issues and warnings that need attention.',
  },
};

/** @deprecated use flat module ids */
export const REPORT_MODULES = INTELLIGENCE_MODULE_IDS.filter((id) => id !== 'accounting').map((id) => ({
  key: id === 'operations-intelligence' ? 'intelligence' : id,
  label: MODULE_META[id]?.title || id,
  description: MODULE_META[id]?.description || '',
  icon: MODULE_META[id]?.icon || '•',
  tone: MODULE_META[id]?.tone || 'blue',
}));

export function moduleNeedsDateRange(moduleId) {
  return moduleId === 'dashboard'
    || moduleId === 'owner-fund-analytics'
    || INTELLIGENCE_MODULE_IDS.includes(moduleId);
}

export function legacyTabToHubModule(tab) {
  if (tab === 'overview') return 'dashboard';
  if (tab === 'operations' || tab === 'reports') return 'owner-dashboard';
  if (tab === 'intelligence') return 'operations-intelligence';
  return tab;
}

export function hubViewToExportKey(moduleId) {
  const map = {
    'owner-dashboard': 'owner-dashboard',
    'business-insights': 'business-insights',
    'data-analytics': 'data-analytics',
    'operations-intelligence': 'intelligence',
    accounting: 'accounting',
  };
  return map[moduleId] || moduleId;
}

export const PUMP_LAUNCHER_MODULES = INTELLIGENCE_MODULE_IDS.map((id) => ({
  key: id,
  title: MODULE_META[id].title,
  description: MODULE_META[id].description,
  module: id,
  icon: MODULE_META[id].icon,
  tone: MODULE_META[id].tone,
})).concat([{
  key: 'owner-fund-analytics',
  title: MODULE_META['owner-fund-analytics'].title,
  description: MODULE_META['owner-fund-analytics'].description,
  module: 'owner-fund-analytics',
  icon: MODULE_META['owner-fund-analytics'].icon,
  tone: MODULE_META['owner-fund-analytics'].tone,
}]);
